import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@unistocks/components/toast';
import { AssetSettingsTabProps } from '@unistocks/features/portfolios/components/asset-settings/tab-props';
import { EditSectionAssetForm } from '@unistocks/features/portfolios/components/forms/edit-asset-form';
import { PortfolioType } from '@unistocks/features/portfolios/portfolio-type';
import {
  invalidatePortfolioQueriesById,
  isPortfolio as isPortfolioCheck
} from '@unistocks/features/portfolios/portfolio-utils';
import { invalidateWatchlistQueriesById } from '@unistocks/features/watchlists/watchlist-utils';
import { t } from '@unistocks/localization';
import { PortfolioDto } from '@unistocks/proxy/axios-client';
import {
  setPortfoliosGetData,
  usePortfoliosUpdateAssetMutation
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import {
  setWatchlistsGetData,
  useWatchlistsUpdateAssetMutation
} from '@unistocks/proxy/axios-client/WatchlistsQuery';
import { memo } from 'react';

export const MyHoldingsTab = memo<AssetSettingsTabProps>(({ portfolio, asset, onOpenChange }) => {
  const queryClient = useQueryClient();
  const portfolioId = portfolio?.id ?? '';
  const assetId = asset?.id ?? '';
  const sectionId = asset?.sectionId ?? '';
  const isPortfolio = isPortfolioCheck(portfolio);

  const { mutateAsync: updatePortfolioAsset } = usePortfoliosUpdateAssetMutation(
    portfolioId,
    sectionId,
    assetId,
    {
      onSuccess: portfolioDto => {
        setPortfoliosGetData(queryClient, () => portfolioDto, portfolioId);
        invalidatePortfolioQueriesById(queryClient, portfolioId);
        onOpenChange?.(false);

        toast.success(t('::SuccessfullyUpdatedAsset', { 0: asset!.name }));
      }
    }
  );

  const { mutateAsync: updateWatchlistAsset } = useWatchlistsUpdateAssetMutation(
    portfolioId,
    sectionId,
    assetId,
    {
      onSuccess: watchlistDto => {
        setWatchlistsGetData(queryClient, () => watchlistDto, portfolioId);
        invalidateWatchlistQueriesById(queryClient, portfolioId);
        onOpenChange?.(false);

        toast.success(t('::SuccessfullyUpdatedAsset', { 0: asset!.name }));
      }
    }
  );

  if (!portfolio || !asset) return null;

  return (
    <EditSectionAssetForm
      portfolioType={portfolio.type as PortfolioType}
      portfolioCurrency={(portfolio as PortfolioDto).currency}
      asset={asset}
      onSubmit={isPortfolio ? updatePortfolioAsset : updateWatchlistAsset}
    />
  );
});

MyHoldingsTab.displayName = 'MyHoldingsTab';
